import { useEffect, useRef, useState } from "react";
import { FaArchive, FaCheck, FaDownload, FaEye, FaEyeSlash, FaExpand, FaFileAlt } from "react-icons/fa";
import { PERSONAL_VAULT_CONSTANTS } from "../_MODULES/_API";
import { OPTIONS_PERSONAL_VAULT_AUTH_MODE, OPTIONS_PERSONAL_VAULT_FILE_PURPOSE, onFindOptionLabel } from "../../../_Catalogs.Views";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { FORMAT_API_ERROR_MESSAGE } from "../../../../../_CORE/API/_ApiUtil/handleAxiosError";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { CopyValue } from "./CopyValue";
import { REACT_HOT_TOAST_ERROR, REACT_HOT_TOAST_SUCCESS } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { UniversalFilePreviewModal } from "../../../../_UI/UniversalFilePreview";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { downloadFilesAsZip } from "../../../../_UI/FileBatchDownload";
import "../../../../_UI/_Styles/FileCollection.css";

const REVEAL_TIMEOUT_MS = 20000;

const formatUsedAt = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
};

export const PersonalVaultRecoveryPanel = ({ entry, accessPin, onChanged }) => {
    const [useRevealedCodes, setRevealedCodes] = useState({});
    const [useBusy, setBusy] = useState(false);
    const [usePreviewFile, setPreviewFile] = useState(null);
    const [useDownloadingFiles, setDownloadingFiles] = useState(false);
    const revealTimerRef = useRef(null);

    const CODES = entry.recoveryCodes ?? [];
    const FILES = entry.files ?? [];
    const AVAILABLE = CODES.filter((code) => !code.isUsed).length;
    const USED = CODES.length - AVAILABLE;
    const HAS_RECOVERY = CODES.length > 0 || FILES.length > 0;
    const AUTH_LABEL = onFindOptionLabel(OPTIONS_PERSONAL_VAULT_AUTH_MODE, entry.authMode || "NONE");

    useEffect(() => () => {
        if (revealTimerRef.current) window.clearTimeout(revealTimerRef.current);
    }, []);

    useEffect(() => {
        const onVisibility = () => {
            if (!document.hidden) return;
            if (revealTimerRef.current) {
                window.clearTimeout(revealTimerRef.current);
                revealTimerRef.current = null;
            }
            setRevealedCodes({});
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    useEffect(() => {
        setRevealedCodes({});
        if (revealTimerRef.current) {
            window.clearTimeout(revealTimerRef.current);
            revealTimerRef.current = null;
        }
    }, [entry.id]);

    const hideRevealedCode = () => {
        if (revealTimerRef.current) {
            window.clearTimeout(revealTimerRef.current);
            revealTimerRef.current = null;
        }
        setRevealedCodes({});
    };

    const onRevealCode = async (code) => {
        if (code.isUsed || !code.hasCode) return;

        if (useRevealedCodes[code.id]) {
            hideRevealedCode();
            return;
        }

        const URL = API_CORE.ENDPOINTS[PERSONAL_VAULT_CONSTANTS.RECOVERY_CODE_REVEAL].replace(":id", code.id);
        const RESPONSE = await API_CORE.SERVICES.POST_RESOURCE(URL, { accessPin: accessPin ?? undefined });
        const VALUE = RESPONSE?.data?.code ?? RESPONSE?.code ?? null;

        if (VALUE) {
            if (revealTimerRef.current) window.clearTimeout(revealTimerRef.current);
            setRevealedCodes({ [code.id]: VALUE });
            revealTimerRef.current = window.setTimeout(() => {
                setRevealedCodes({});
                revealTimerRef.current = null;
            }, REVEAL_TIMEOUT_MS);
            return;
        }

        if (RESPONSE?.success === false) {
            REACT_HOT_TOAST_ERROR(FORMAT_API_ERROR_MESSAGE(RESPONSE, "No se pudo revelar el código"), { duration: 7000 });
        }
    };

    const onConsumeCode = async (code) => {
        if (code.isUsed || useBusy) return;

        const OK = await confirm.danger(
            "Marcar código como utilizado",
            "Esta acción es permanente. El código quedará como utilizado y el backend destruirá su secreto cifrado.",
        );
        if (!OK) return;

        setBusy(true);
        const URL = API_CORE.ENDPOINTS[PERSONAL_VAULT_CONSTANTS.RECOVERY_CODE_CONSUME].replace(":id", code.id);
        const RESPONSE = await API_CORE.SERVICES.POST_RESOURCE(URL, { accessPin: accessPin ?? undefined });
        setBusy(false);

        if (RESPONSE?.success === false) {
            REACT_HOT_TOAST_ERROR(FORMAT_API_ERROR_MESSAGE(RESPONSE, "No se pudo consumir el código"), { duration: 7000 });
            return;
        }

        hideRevealedCode();
        REACT_HOT_TOAST_SUCCESS("Código marcado como utilizado");
        const updated = RESPONSE?.data ?? RESPONSE ?? {};
        onChanged?.({
            recoveryCodes: CODES.map((item) => String(item.id) === String(code.id)
                ? { ...item, ...updated, isUsed: true, hasCode: false }
                : item),
        });
    };

    const onArchiveCode = async (code) => {
        if (code.isUsed || useBusy) return;

        const OK = await confirm.danger(
            "Archivar código",
            "Se archiva este código de recuperación. Podés restaurarlo desde Archivados.",
        );
        if (!OK) return;

        const URL = API_CORE.ENDPOINTS[PERSONAL_VAULT_CONSTANTS.RECOVERY_CODE_ARCHIVE].replace(":id", code.id);
        const RESPONSE = await API_CORE.SERVICES.PATCH_resource(URL, { accessPin: accessPin ?? undefined });
        if (RESPONSE?.success === false) {
            REACT_HOT_TOAST_ERROR(FORMAT_API_ERROR_MESSAGE(RESPONSE, "No se pudo archivar el código"), { duration: 7000 });
            return;
        }

        hideRevealedCode();
        REACT_HOT_TOAST_SUCCESS("Código archivado");
        onChanged?.({ recoveryCodes: CODES.filter((item) => String(item.id) !== String(code.id)) });
    };

    const onLoadFileBlob = async (file) => {
        if (!file?.id) throw new Error("Archivo sin identificador");
        const URL = API_CORE.ENDPOINTS[PERSONAL_VAULT_CONSTANTS.FILE_DOWNLOAD].replace(":id", file.id);
        const BLOB = await API_CORE.SERVICES.POST_RESOURCE(
            URL,
            { accessPin: accessPin ?? undefined },
            { responseType: "blob" },
        );
        if (!(BLOB instanceof Blob)) throw new Error("Archivo inválido");
        return BLOB;
    };

    const onDownloadFile = async (file) => {
        try {
            const BLOB = await onLoadFileBlob(file);
            const OBJECT_URL = window.URL.createObjectURL(BLOB);
            const LINK = window.document.createElement("a");
            LINK.href = OBJECT_URL;
            LINK.download = file.originalName || file.title || "archivo";
            window.document.body.appendChild(LINK);
            LINK.click();
            window.document.body.removeChild(LINK);
            window.URL.revokeObjectURL(OBJECT_URL);
        } catch {
            REACT_HOT_TOAST_ERROR("No se pudo descargar el archivo");
        }
    };

    const onDownloadAllFiles = async () => {
        if (!FILES.length || useDownloadingFiles) return;
        setDownloadingFiles(true);
        try {
            await downloadFilesAsZip({
                files: FILES,
                loadBlob: onLoadFileBlob,
                fileName: `${entry.title || entry.name || "recuperacion"}-archivos.zip`,
                successMessage: `Se descargaron ${FILES.length} archivo${FILES.length === 1 ? "" : "s"} de recuperación.`,
                errorMessage: "No se pudieron descargar todos los archivos de recuperación.",
            });
        } finally {
            setDownloadingFiles(false);
        }
    };

    const onArchiveFile = async (file) => {
        const OK = await confirm.danger(
            "Archivar archivo",
            `Se archiva "${file.originalName || file.title}". Podés restaurarlo desde Archivados.`,
        );
        if (!OK) return;

        const URL = API_CORE.ENDPOINTS[PERSONAL_VAULT_CONSTANTS.FILE_ARCHIVE].replace(":id", file.id);
        const RESPONSE = await API_CORE.SERVICES.PATCH_resource(URL, { accessPin: accessPin ?? undefined });
        if (RESPONSE?.success === false) {
            REACT_HOT_TOAST_ERROR(RESPONSE?.message || "No se pudo archivar el archivo");
            return;
        }

        REACT_HOT_TOAST_SUCCESS("Archivo archivado");
        onChanged?.({ files: FILES.filter((item) => String(item.id) !== String(file.id)) });
    };

    if (!HAS_RECOVERY) return null;

    return (
        <div className="LStudio__app_vault_recovery_panel LStudio__app_vault_recovery_panel--readonly">
            <div className="LStudio__app_vault_recovery_head">
                <div>
                    <span className="LStudio__app_vault_notes_label">Recuperación guardada</span>
                    {entry.authMode && entry.authMode !== "NONE" && <strong>{AUTH_LABEL}</strong>}
                </div>
                <div className="LStudio__app_vault_recovery_badges">
                    {CODES.length > 0 && <Badge size="sm" tone={AVAILABLE <= 2 ? "warn" : "accent"}>{AVAILABLE} disponibles</Badge>}
                    {USED > 0 && <Badge size="sm" tone="neutral">{USED} usados</Badge>}
                    {FILES.length > 0 && <Badge size="sm" tone="neutral">{FILES.length} archivo{FILES.length === 1 ? "" : "s"}</Badge>}
                </div>
            </div>

            {CODES.length > 0 && AVAILABLE <= 2 && (
                <div className="LStudio__app_vault_recovery_warning" data-empty={AVAILABLE === 0 ? "true" : "false"}>
                    {AVAILABLE === 0
                        ? "No quedan códigos disponibles. Agregá o reemplazá el conjunto desde Editar acceso."
                        : `Quedan sólo ${AVAILABLE} códigos disponibles. Podés agregar un nuevo conjunto desde Editar acceso.`}
                </div>
            )}

            {CODES.length > 0 && (
                <div className="LStudio__app_vault_recovery_codes">
                    {CODES.map((code) => {
                        const VALUE = useRevealedCodes[code.id];
                        const USED_AT = formatUsedAt(code.usedAt);
                        return (
                            <div key={code.id} className="LStudio__app_vault_recovery_code LStudio__app_vault_recovery_code--readonly" data-used={code.isUsed ? "true" : "false"}>
                                <span className="LStudio__app_vault_recovery_code_label">{code.label || "Código"}</span>
                                <div className="LStudio__app_vault_recovery_code_value">
                                    {code.isUsed
                                        ? <span>UTILIZADO</span>
                                        : VALUE
                                            ? <CopyValue value={VALUE} label="Copiar código" />
                                            : <span>••••••••</span>}
                                </div>

                                {!code.isUsed ? (
                                    <AppButton size="sm" variant="ghost" tone="neutral"
                                        icon={VALUE ? <FaEyeSlash /> : <FaEye />} isIconOnly
                                        aria-label={VALUE ? "Ocultar código" : "Revelar código"}
                                        onClick={() => onRevealCode(code)} />
                                ) : <span className="LStudio__app_vault_recovery_code_eye_placeholder" />}

                                <div className="LStudio__app_vault_recovery_code_status">
                                    {code.isUsed ? (
                                        <span>{USED_AT ? `Usado ${USED_AT}` : "Uso permanente"}</span>
                                    ) : (
                                        <CardActionMenu title="Acciones del código" items={[
                                            { key: "used", icon: <FaCheck />, label: "Marcar como utilizado", onClick: () => onConsumeCode(code) },
                                            { divider: true },
                                            { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchiveCode(code) },
                                        ]} />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {FILES.length > 0 && (
                <div className="LStudio__app_vault_recovery_files LStudio__app_vault_recovery_files--readonly LStudio__app_file_collection">
                    <div className="LStudio__app_file_collection_header">
                        <span className="LStudio__app_vault_notes_label">Archivos de recuperación</span>
                        <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaDownload />}
                            isLoading={useDownloadingFiles} onClick={onDownloadAllFiles}>Descargar todos</AppButton>
                    </div>
                    <div className="LStudio__app_vault_recovery_file_list LStudio__app_file_scroll_area">
                        {FILES.map((file) => (
                            <div key={file.id} className="LStudio__app_vault_recovery_file_row">
                                <FaFileAlt />
                                <button type="button" onClick={() => setPreviewFile(file)} title="Abrir vista previa">
                                    {file.originalName || file.title}
                                </button>
                                <Badge size="sm" tone="neutral">
                                    {onFindOptionLabel(OPTIONS_PERSONAL_VAULT_FILE_PURPOSE, file.purpose || "GENERAL")}
                                </Badge>
                                {file.isEncrypted && <Badge size="sm" tone="accent">Cifrado</Badge>}
                                <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaExpand />} isIconOnly
                                    aria-label="Vista previa" title="Vista previa" onClick={() => setPreviewFile(file)} />
                                <CardActionMenu title="Acciones del archivo" items={[
                                    { key: "download", icon: <FaDownload />, label: "Descargar", onClick: () => onDownloadFile(file) },
                                    { divider: true },
                                    { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchiveFile(file) },
                                ]} />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <UniversalFilePreviewModal
                file={usePreviewFile}
                isOpen={!!usePreviewFile}
                onClose={() => setPreviewFile(null)}
                loadBlob={onLoadFileBlob}
                onDownload={onDownloadFile}
            />
        </div>
    );
};
